import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  BadgeCheck, CheckCircle2, Clock, DoorOpen, Loader2, ReceiptText, XCircle,
} from 'lucide-react'
import HomeShell from './HomeShell'
import DiscordIcon from '@/components/site/DiscordIcon'
import useDiscordInvite from '@/components/site/useDiscordInvite'
import { Button } from '@/components/ui/button'
import { useLanguage } from '@/contexts/LanguageContext'
import { HOME_LOGIN_URL, useHomeAuth } from '@/contexts/HomeAuthContext'
import { SUPPORTER_ENABLED } from '@/lib/features'

/**
 * turksletloose.com/supporter/thanks — where Whop drops the buyer once checkout
 * is over, one way or the other.
 *
 * Landing here is not proof that anything was paid: the address is guessable,
 * a redirect can fire on a cancelled session, and Whop takes a moment to make a
 * fresh membership readable through its API. So the page never trusts the
 * redirect — it reads /api/home/me and reports whichever of the five states it
 * actually found.
 *
 * The distinction that matters is `pending` vs `failed`. "We cannot see your
 * payment yet" is the honest reading of a poll that came back empty: from here
 * a completed payment mid-propagation and an abandoned checkout look identical,
 * and telling someone who just paid that their payment failed is the worse of
 * the two mistakes. `failed` is stated only when Whop says so itself, through
 * ?status=cancel on the way back.
 *
 * Nothing on this page grants anything — the supporter role is Whop's Discord
 * app's job. This is the receipt and the "what now", not the entitlement.
 */

/** Roughly 15s of polling: long enough for Whop to catch up, short enough to
 *  not leave a spinner sitting there while somebody wonders about their money. */
const POLL_TRIES = 6
const POLL_INTERVAL_MS = 2500

type State = 'checking' | 'success' | 'pending' | 'failed' | 'signedOut'

/** Forced states for review, via ?demo=. Never reached by a real buyer: the
 *  banner says so on screen, and nothing is read from the API in this mode. */
const DEMO_STATES: State[] = ['checking', 'success', 'pending', 'failed', 'signedOut']

export default function SupporterReturn() {
  const { T } = useLanguage()
  const t = T.supporter.ret
  const inviteUrl = useDiscordInvite()
  const { user, loading, refresh } = useHomeAuth()
  const [params] = useSearchParams()

  const demo = params.get('demo') as State | null
  const isDemo = Boolean(demo && DEMO_STATES.includes(demo))
  // Whop sends the buyer back with this when they leave without paying. It is
  // the only signal that separates "not paid" from "not visible yet".
  const cancelled = params.get('status') === 'cancel'

  const [tries, setTries] = useState(0)
  const [busy, setBusy] = useState(false)

  // The provider fetched /api/home/me once on mount; that answer predates the
  // purchase. Poll it until the membership turns up or the tries run out.
  const timer = useRef<number | null>(null)
  useEffect(() => {
    if (isDemo || cancelled || loading) return
    if (!user || user.supporter?.active) return
    if (tries >= POLL_TRIES) return

    timer.current = window.setTimeout(() => {
      setTries(n => n + 1)
      void refresh()
    }, POLL_INTERVAL_MS)
    return () => { if (timer.current) window.clearTimeout(timer.current) }
  }, [isDemo, cancelled, loading, user, tries, refresh])

  const recheck = useCallback(async () => {
    setBusy(true)
    try { await refresh() } finally { setBusy(false) }
  }, [refresh])

  function resolveState(): State {
    if (isDemo) return demo as State
    if (loading) return 'checking'
    if (!user) return 'signedOut'
    if (user.supporter?.active) return 'success'
    if (cancelled) return 'failed'
    return tries >= POLL_TRIES ? 'pending' : 'checking'
  }

  const state = resolveState()

  const successSteps = [
    { icon: BadgeCheck, title: t.successStep1Title, body: t.successStep1Body },
    { icon: DoorOpen,   title: t.successStep2Title, body: t.successStep2Body },
    { icon: ReceiptText, title: t.successStep3Title, body: t.successStep3Body },
  ]

  /** One panel, five faces. The icon and its colour carry the verdict; the
   *  buttons underneath carry the only thing left to do about it. */
  function panel() {
    switch (state) {
      case 'checking':
        return {
          icon: <Loader2 className="h-7 w-7 animate-spin text-primary" />,
          title: t.checkingTitle,
          body: t.checkingBody,
        }
      case 'success':
        return {
          // Green, not `text-primary`: --primary is a red (oklch hue 28), and a
          // red tick over "thank you" reads as a failure at a glance. This is
          // the one place on the site where the colour has to mean "went
          // through" rather than "our accent".
          icon: <CheckCircle2 className="h-7 w-7 text-emerald-400" />,
          title: t.successTitle,
          body: t.successBody,
        }
      case 'pending':
        return {
          icon: <Clock className="h-7 w-7 text-primary" />,
          title: t.pendingTitle,
          body: t.pendingBody,
        }
      case 'failed':
        return {
          icon: <XCircle className="h-7 w-7 text-destructive" />,
          title: t.failedTitle,
          body: t.failedBody,
        }
      case 'signedOut':
        return {
          icon: <DiscordIcon size={26} />,
          title: t.signedOutTitle,
          body: t.signedOutBody,
        }
    }
  }

  const p = panel()

  return (
    <HomeShell pageTitle={t.pageTitle}>
      <section className="mx-auto w-full max-w-3xl px-5 pb-20 pt-16 md:pb-28 md:pt-24">
        {isDemo && (
          <p className="mb-6 rounded-sm border border-primary/40 bg-primary/10 px-4 py-2.5 text-center font-display text-[11px] font-bold tracking-[0.14em] text-primary">
            {t.demoBadge}
          </p>
        )}

        <div className="rounded-md border border-white/12 bg-card/85 p-7 md:p-10">
          <div className="flex flex-col items-center text-center">
            {p.icon}
            <h1 className="mt-5 font-display text-2xl font-extrabold uppercase leading-tight tracking-tight md:text-4xl">
              {p.title}
            </h1>
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-foreground/80">
              {p.body}
            </p>
            {/* Only the success state has a second paragraph: what is already in
                motion on the staff side, before the three cards break it down. */}
            {state === 'success' && (
              <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-foreground/80">
                {t.successBody2}
              </p>
            )}
          </div>

          {/* What happens next, only where there is a next: the three things the
              purchase set in motion, none of which need the buyer to act. */}
          {state === 'success' && (
            <div className="mt-9 grid gap-px overflow-hidden rounded-md border border-white/10 bg-white/10">
              {successSteps.map(s => (
                <div key={s.title} className="flex items-start gap-4 bg-card/80 p-5">
                  <s.icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div>
                    <h2 className="font-display text-[15px] font-bold tracking-tight">{s.title}</h2>
                    <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            {state === 'success' && (
              <>
                <Link to="/profile">
                  <Button size="lg" className="gap-2 font-semibold transition-transform active:scale-95">
                    {t.goProfile}
                  </Button>
                </Link>
                {inviteUrl && (
                  <a href={inviteUrl} target="_blank" rel="noreferrer">
                    <Button size="lg" variant="outline" className="gap-3">
                      <DiscordIcon />
                      {T.home.discordJoinCta}
                    </Button>
                  </a>
                )}
              </>
            )}

            {state === 'pending' && (
              <>
                <Button
                  size="lg"
                  className="gap-2 font-semibold transition-transform active:scale-95"
                  disabled={busy}
                  onClick={() => void recheck()}
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t.recheck}
                </Button>
                <Link to="/profile">
                  <Button size="lg" variant="outline">{t.goProfile}</Button>
                </Link>
              </>
            )}

            {state === 'failed' && (
              <>
                {/* No retry while the membership is switched off: /supporter is
                    not routed then, so the button would bounce to the landing
                    page and read as the site losing the click. */}
                {SUPPORTER_ENABLED && (
                  <Link to="/supporter">
                    <Button size="lg" className="gap-2 font-semibold transition-transform active:scale-95">
                      {t.retry}
                    </Button>
                  </Link>
                )}
                <Link to="/">
                  <Button size="lg" variant="outline">{T.supporter.backHome}</Button>
                </Link>
              </>
            )}

            {state === 'signedOut' && (
              <a href={HOME_LOGIN_URL}>
                <Button size="lg" className="gap-3 font-semibold transition-transform active:scale-95">
                  <DiscordIcon />
                  {T.supporter.ctaSignIn}
                </Button>
              </a>
            )}
          </div>

          {/* The escape hatch, on every state that can go wrong. Same wording as
              the product page's "something wrong?" note: a support ticket. */}
          {(state === 'success' || state === 'pending' || state === 'failed') && (
            <p className="mt-7 text-center text-[12px] leading-relaxed text-muted-foreground/75">
              {state === 'success' ? t.successNote : state === 'pending' ? t.pendingNote : t.failedNote}
            </p>
          )}
        </div>
      </section>
    </HomeShell>
  )
}
